import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import { Box, IconButton, type Theme, useMediaQuery } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoleNames } from '../../config/role-names';
import { useMemberships } from '../../hooks/auth/use-memberships';
import { useSwitchTenant } from '../../hooks/auth/use-switch-tenant';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { tenantContextAtom } from '../../stores/auth.store';
import { colors } from '../../theme/ai-theme';
import { CompanyAdvancedSearchDialog } from '../company-picker/company-advanced-search-dialog';
import { CompanyPickerAutocomplete } from '../company-picker/company-picker-autocomplete';

export function TenantSelector() {
  const tenantContext = useAtomValue(tenantContextAtom);
  const { t } = useTranslation();
  const { memberships, isLoading } = useMemberships();
  const { switchTenant } = useSwitchTenant();
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const isMobile = useIsMobile();
  // Sotto md, accanto al drawer, la barra non ha spazio per il campo di ricerca.
  const isNarrowHeader = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const showIconTrigger = isMobile || isNarrowHeader;

  // Il consulente deve sempre vedere su quale cliente sta operando, anche
  // quando gliene resta uno solo (es. dopo una revoca) — per tutti gli altri
  // ruoli il selettore è inutile finché non c'è scelta da fare.
  const isConsultant = memberships.some(
    (membership) => membership.roleName === RoleNames.CONSULTANT,
  );
  if (!isLoading && memberships.length <= 1 && !isConsultant) {
    return null;
  }

  const currentTenantId = tenantContext?.currentTenantId;
  const currentTenantName = tenantContext?.currentTenantName ?? t('consultant.selectCompany');

  function handleSelect(tenantId: string, tenantName: string) {
    switchTenant({ tenantId, tenantName });
  }

  const iconButtonSx = {
    width: 48,
    height: 48,
    color: colors.navigation.text,
    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {showIconTrigger ? (
        <IconButton
          onClick={() => setIsAdvancedSearchOpen(true)}
          aria-label={t('consultant.switchCompanyCurrent', { name: currentTenantName })}
          title={currentTenantName}
          sx={iconButtonSx}
        >
          <BusinessOutlinedIcon />
        </IconButton>
      ) : (
        <>
          <CompanyPickerAutocomplete
            variant="navbar"
            currentTenantId={currentTenantId}
            currentTenantName={currentTenantName}
            onSelect={handleSelect}
            onOpenAdvancedSearch={() => setIsAdvancedSearchOpen(true)}
          />
          <IconButton
            onClick={() => setIsAdvancedSearchOpen(true)}
            aria-label={t('consultant.companyPicker.advancedSearch')}
            sx={{ ...iconButtonSx, ml: 0.5 }}
          >
            <ManageSearchOutlinedIcon />
          </IconButton>
        </>
      )}
      <CompanyAdvancedSearchDialog
        open={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
        currentTenantId={currentTenantId}
        onSelect={handleSelect}
      />
    </Box>
  );
}
